import * as React from 'react';

import { QuoteStatus } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  Checkbox,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@rematter/ui-web';
import { LucideAlertCircle, LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';
import { colorByQuoteStatus } from '../../utils/status';

export type DeletableQuote = {
  id: string;
  quoteNumber: string;
  status: QuoteStatus;
};

type QuoteDeleteDialogProps = {
  quotes: Array<DeletableQuote>;
  onDelete: (id: string) => Promise<void>;
  onComplete?: () => void;
  onCancel?: () => void;
};

type QuoteRowProps = {
  quote: DeletableQuote;
  quoteLabel: string;
  isSingleQuote: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
};

//
// ⚛️ ⚛️ ⚛️ QUOTE ROW ⚛️ ⚛️ ⚛️
//
const QuoteRow: React.FC<QuoteRowProps> = ({
  quote,
  quoteLabel,
  isSingleQuote,
  isSelected,
  isDisabled,
  onToggle,
}) => {
  const t = useTranslations();

  const content = (
    <div className="@QuoteRowContent flex w-full flex-1 justify-between font-semibold">
      <span>
        {quoteLabel}
        {quote.quoteNumber}
      </span>
      <Badge color={colorByQuoteStatus[quote.status]} size="small">
        {
          t.common.status[
            quote.status.toLowerCase() as keyof typeof t.common.status
          ]
        }
      </Badge>
    </div>
  );

  if (isSingleQuote) {
    return <div className="@QuoteRow flex flex-col items-start">{content}</div>;
  }

  return (
    <div className="@QuoteRow flex flex-col items-start">
      <Checkbox
        isSelected={isSelected}
        onChange={onToggle}
        isDisabled={isDisabled}
      >
        {content}
      </Checkbox>
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ QUOTE DELETE DIALOG ⚛️ ⚛️ ⚛️
//
export const QuoteDeleteDialog: React.FC<QuoteDeleteDialogProps> = ({
  quotes,
  onDelete,
  onComplete,
  onCancel,
}) => {
  const t = useTranslations();
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(
    () => new Set(quotes.map((q) => q.id)),
  );

  const isSingleQuote = quotes.length === 1;

  const handleToggle = React.useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleConfirm = async () => {
    if (selectedIds.size === 0) return;

    setIsLoading(true);
    try {
      const selected = quotes.filter((q) => selectedIds.has(q.id));
      await Promise.all(selected.map((q) => onDelete(q.id)));
      onComplete?.();
    } finally {
      setIsLoading(false);
    }
  };

  const {
    title,
    titleBulk,
    warning,
    quoteLabel,
    confirm,
    confirmBulk,
    cancel,
  } = t.quotes.deleteDialog;

  return (
    <DialogOverlay isOpen className="@QuoteDeleteDialog">
      <DialogContent className="max-w-2xl min-w-sm" closeButton={false}>
        <DialogHeader>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <LucideAlertCircle className="size-5 fill-failure text-white" />
            {isSingleQuote ? title : titleBulk(quotes.length)}
          </div>
          <div className="text-sm text-neutral/75">{warning}</div>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-4">
          <div className="flex flex-col gap-2 rounded-md bg-neutral/5 p-2.5">
            {quotes.map((quote) => (
              <QuoteRow
                key={quote.id}
                quote={quote}
                quoteLabel={quoteLabel}
                isSingleQuote={isSingleQuote}
                isSelected={selectedIds.has(quote.id)}
                isDisabled={isLoading}
                onToggle={() => handleToggle(quote.id)}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-4">
          <Button
            variant="text"
            color="neutral"
            onPress={onCancel}
            isDisabled={isLoading}
          >
            {cancel}
          </Button>
          <Button
            variant="solid"
            color="failure"
            onPress={handleConfirm}
            isDisabled={selectedIds.size === 0 || isLoading}
            isLoading={isLoading}
            icon={<LucideTrash2 className="size-4" />}
          >
            {isSingleQuote ? confirm : confirmBulk(selectedIds.size)}
          </Button>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
